/**
 * RunCostTab — run-level totalCostUsd + per-step cost attribution via llmCalls.
 * Per-step cost is now available via payload.llmCalls (OBS-6 stage 1).
 * Aggregates cost_usd per step_id from the llmCalls block.
 */
import React, { useMemo } from 'react';
import { DollarSign } from 'lucide-react';
import { type DebugPayload, formatCost, formatDuration, mapStepStatus } from './run-detail-types.js';

interface RunCostTabProps {
  payload: DebugPayload;
}

export const RunCostTab: React.FC<RunCostTabProps> = ({ payload }) => {
  const { run, graph, llmCalls } = payload;
  const steps = graph.steps;
  const totalCost = formatCost(run.totalCostUsd);

  // Build a map of stepId → summed cost from the llmCalls block
  const stepCostMap = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    if (!llmCalls) return map;
    for (const call of llmCalls.calls) {
      if (!call.stepId || call.costUsd == null) continue;
      const n = parseFloat(call.costUsd);
      if (!Number.isFinite(n)) continue;
      map.set(call.stepId, (map.get(call.stepId) ?? 0) + n);
    }
    return map;
  }, [llmCalls]);

  const hasCostData = llmCalls != null && llmCalls.calls.length > 0;

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden mb-4" style={{ margin: 0 }}>
      {/* Card header */}
      <div className="flex items-center justify-between gap-2.5 px-4 py-3 border-b border-border bg-surface-alt">
        <span className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-muted flex items-center gap-2">
          <DollarSign size={14} className="text-faint" />
          Cost by step
        </span>
        <span className="font-mono text-[10.5px] text-faint">total {totalCost}</span>
      </div>

      <div className="p-4 space-y-3">
        {/* Run total — prominent */}
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[22px] font-semibold text-heading leading-none">{totalCost}</span>
          <span className="text-[12px] text-muted">run total</span>
          {llmCalls && llmCalls.totalCount > llmCalls.cappedAt && (
            <span className="text-[11px] text-warning ml-2">
              (showing newest {llmCalls.cappedAt} of {llmCalls.totalCount} LLM calls)
            </span>
          )}
        </div>

        {/* Step rows — name + duration + cost */}
        {steps.length > 0 && (
          <div className="mt-1">
            {/* Header */}
            <div
              className="grid gap-4 px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-[0.6px] text-faint border-b border-border"
              style={{ gridTemplateColumns: 'minmax(0,1fr) 70px 80px' }}
            >
              <span>Step</span>
              <span className="text-right">Duration</span>
              <span className="text-right">LLM Cost</span>
            </div>
            {steps.map((step) => {
              const status = mapStepStatus(step.status);
              const stepCost = stepCostMap.get(step.stepId);
              return (
                <div
                  key={step.stepId ?? step.id}
                  className={[
                    'grid gap-4 px-3 py-2 border-b border-border last:border-0 text-[12px]',
                    status === 'failed' ? 'shadow-[inset_3px_0_0] shadow-danger' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ gridTemplateColumns: 'minmax(0,1fr) 70px 80px' }}
                >
                  <span className="text-body truncate">{step.stepName}</span>
                  <span className="text-right font-mono text-muted">{formatDuration(step.durationMs)}</span>
                  <span className="text-right font-mono text-muted">
                    {hasCostData ? (stepCost != null ? formatCost(stepCost) : '—') : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* If no llmCalls data at all (old runs / non-debug detail) */}
        {!hasCostData && (
          <p className="text-[11.5px] text-muted">
            Per-step cost attribution requires debug or full detail level and at least one recorded LLM call.
          </p>
        )}
      </div>
    </div>
  );
};
